import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useEffect } from "react";

import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Card,
  Button,
  TextField,
  CardHeader,
  Typography,
  CardContent
} from '@mui/material';

import { useBoolean } from "src/hooks/use-boolean";

import { useTranslate } from "src/locales";
import { useSearchLabelsQuery } from "src/services/utils/utils.service";
import { EntityLabel, EntityLabelType } from "src/services/utils/utils.types";
import { useGetOrganizationCandidateQuery, useUpdateUnlinkedCandidateMutation } from "src/services/candidates/candidates.service";

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import EmptyContent from "src/components/empty-content";
import { ConfirmDialog } from "src/components/custom-dialog";

type Props = {
  candidateId: string;
};

interface LabelOption {
  label: string;
  value: string;
}

export const CandidateLabelsCard = ({ candidateId }: Props) => {
  const { t } = useTranslate();

  const showEditorDialog = useBoolean(false);

  const [selectedLabels, setSelectedLabels] = useState<LabelOption[]>([]);
  const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

  const { currentData: orgCandidate, isLoading } = useGetOrganizationCandidateQuery(
    candidateId,
    {
      skip: !candidateId,
    }
  );

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelSearchQuery ?? undefined,
    type: EntityLabelType.CANDIDATE,
    page: 1,
    per_page: 10
  });

  const [updateCandidate, { isLoading: isUpdatingCandidate }] = useUpdateUnlinkedCandidateMutation();

  useEffect(() => {
    if (orgCandidate?.labels) {
      const labels = orgCandidate.labels.map((label: EntityLabel) => ({
        label: label.label as string,
        value: label.id as string
      }));

      setSelectedLabels(labels);
    }
  }, [orgCandidate, t]);

  const selectableLabels = useMemo(() => {
    if (!availableLabels?.results) return [];

    return availableLabels.results.map((label: EntityLabel) => ({
      label: label.label as string,
      value: label.id as string,
    }));
  }, [availableLabels]);

  const handleUpdateLabels = async () => {

    if (!orgCandidate) return;

    try {

      const labels = selectedLabels.map((label: LabelOption) => ({
        id: label.value
      }));

      await updateCandidate({
        candidateId,
        data: {
          labels
        }
      }).unwrap();

      setSelectedLabels([]);

      enqueueSnackbar(t('candidates.api.update_labels.success'), {
        variant: 'success'
      });

      showEditorDialog.onFalse();
    }
    catch (error) {
      enqueueSnackbar(t('candidates.api.update_labels.default_error'), {
        variant: 'error'
      });
    }
  }

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setLabelSearchQuery(value);
    }, 500),
    [setLabelSearchQuery]
  );

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
            <Iconify icon="tabler:tags" />
            <Box>{t('candidates.detail.labels.title')}</Box>
          </Stack>
        }
        action={
          <Button
            variant="outlined"
            onClick={showEditorDialog.onTrue}
            size='small'
            startIcon={<Iconify icon="eva:edit-2-outline" />}
          >

            {t('common.edit')}
          </Button>
        }
      />
      <CardContent>
        <Box width="100%" >

          {
            orgCandidate?.labels?.length === 0 && (
              <Stack direction="row" spacing={2} width="100%" justifyContent="center" alignItems="center" flexWrap="wrap">
                <EmptyContent
                  title={t('companies.profile.labels.empty')}
                  sx={{ width: '100%' }}
                />
              </Stack>
            )
          }

          {
            !!orgCandidate?.labels?.length && (
              <Stack direction="row" spacing={2} width="100%" justifyContent="start" alignItems="center" flexWrap="wrap">
                {
                  orgCandidate?.labels?.map((label) => (
                    <Label key={label.id as string} variant="soft">{label.label}</Label>
                  ))
                }
              </Stack>
            )
          }


        </Box>
      </CardContent>

      <ConfirmDialog
        open={showEditorDialog.value}
        title={t('candidates.detail.labels.dialog_title')}
        onClose={showEditorDialog.onFalse}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            multiple
            options={selectableLabels}
            value={selectedLabels}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'candidates.detail.labels.select'
                )}
                placeholder={t(
                  'candidates.detail.labels.select_placeholder'
                )}
              />
            )}
            onInputChange={(_e, value) => onSearchQueryChange(value)}
            onChange={(_e, value: LabelOption[]) => setSelectedLabels(value)}
            renderOption={({ key, ...rest }, option: LabelOption) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.label}`}
                  </Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => option.label as string}
            isOptionEqualToValue={(option: LabelOption, value: LabelOption) =>
              option && option.value === value.value
            }
            getOptionKey={(option) => (option as LabelOption).value as string}
          />
        }
        action={
          <Button variant="contained" disabled={!selectableLabels.length} onClick={handleUpdateLabels}>
            {t('common.update')}
          </Button>
        }
      />
    </Card>
  );
}